import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { User, Home, CalendarDays, Gift, Check, Sparkles, FileText, Zap, AlertTriangle, ClipboardCheck, Tag, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { formatPhoneNumber, TIME_SLOTS } from "./constants";
import { useState } from "react";
import { toast } from "sonner";

// Helper to convert square footage value to display range
const getSqftRangeLabel = (sqft) => {
  if (!sqft) return "Not specified";
  if (sqft <= 1000) return "Under 1,500 sq ft";
  if (sqft <= 2000) return "1,500 - 2,500 sq ft";
  if (sqft <= 3000) return "2,500 - 3,500 sq ft";
  if (sqft <= 4000) return "3,500 - 4,500 sq ft";
  return "Over 4,500 sq ft";
};

export default function ReviewStep({
  customerInfo,
  propertyDetails,
  preferredDates,
  pricingConfig,
  selectedServices,
  calculatedPrices,
  subtotal,
  bundleDiscount,
  total,
  showScrubClub,
  estimateAccepted,
  setEstimateAccepted,
  getFullAddress,
  customerNotes,
  setCustomerNotes,
  rushBooking,
  creditCode,
  setCreditCode,
  creditApplied,
  setCreditApplied,
  promoCode,
  promoDiscount,
}) {
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [creditInfo, setCreditInfo] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const validateCreditCode = async () => {
    if (!creditCode || creditCode.trim().length < 3) {
      toast.error("Please enter a valid credit code");
      return;
    }

    setIsValidatingCode(true);
    try {
      const response = await fetch(`${API_URL}/api/credits/validate/${creditCode.trim()}`);
      const data = await response.json();
      
      if (data.valid) {
        setCreditInfo(data.credit);
        setCreditApplied(data.credit.amount);
        toast.success(data.message);
      } else {
        setCreditInfo(null);
        setCreditApplied(0);
        toast.error(data.message);
      }
    } catch (error) {
      toast.error("Error validating credit code");
      setCreditInfo(null);
      setCreditApplied(0);
    } finally {
      setIsValidatingCode(false);
    }
  };

  const removeCreditCode = () => {
    setCreditCode("");
    setCreditInfo(null);
    setCreditApplied(0);
  };

  const finalTotal = Math.max(0, total - creditApplied);

  return (
    <div className="max-w-2xl mx-auto animate-fade-in-up" data-testid="step-4-summary">
      <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
        Your Quote Summary
      </h2>
      <p className="text-slate-600 mb-8">Review your selections and submit your quote</p>

      {/* Customer Info Card */}
      <Card className="border-slate-200 mb-6">
        <CardContent className="p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-slate-400" />
            Customer Information
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Name</p>
              <p className="font-medium text-slate-900">{customerInfo.first_name} {customerInfo.last_name}</p>
            </div>
            <div>
              <p className="text-slate-500">Email</p>
              <p className="font-medium text-slate-900">{customerInfo.email}</p>
            </div>
            <div>
              <p className="text-slate-500">Phone</p>
              <p className="font-medium text-slate-900">{formatPhoneNumber(customerInfo.phone)}</p>
            </div>
            <div>
              <p className="text-slate-500">Address</p>
              <p className="font-medium text-slate-900">{getFullAddress()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Property Details Card */}
      <Card className="border-slate-200 mb-6">
        <CardContent className="p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Home className="w-5 h-5 text-slate-400" />
            Property Details
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Square Footage</p>
              <p className="font-medium text-slate-900">
                {getSqftRangeLabel(propertyDetails.verified_sqft || propertyDetails.square_footage)}
                {propertyDetails.verified_sqft && (
                  <Badge variant="secondary" className="ml-2 text-xs">Verified</Badge>
                )}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Stories</p>
              <p className="font-medium text-slate-900">{propertyDetails.stories} {propertyDetails.stories === 1 ? 'Story' : 'Stories'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preferred Dates Card */}
      <Card className="border-slate-200 mb-6">
        <CardContent className="p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-slate-400" />
            Preferred Appointment Times
          </h3>
          
          {/* Rush Booking Alert - Show ONLY this when rush is selected */}
          {rushBooking ? (
            <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-bold text-amber-800 flex items-center gap-2">
                    Rush Service Requested
                    <Badge className="bg-amber-500 text-white text-xs">ASAP</Badge>
                  </p>
                  <p className="text-sm text-amber-700 mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Premium rates may apply if we can accommodate your schedule
                  </p>
                  <p className="text-xs text-amber-600 mt-2">
                    Our team will contact you within 24 hours to discuss expedited scheduling options.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Regular date preferences - only show when NOT rush booking */
            preferredDates.filter(d => d.date && d.time_slot).length > 0 ? (
              <div className="space-y-2">
                {preferredDates.filter(d => d.date && d.time_slot).map((slot, idx) => (
                  <div key={`slot-${format(slot.date, 'yyyy-MM-dd')}-${slot.time_slot}`} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      {idx + 1}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {format(slot.date, 'EEEE, MMMM d, yyyy')}
                      </p>
                      <p className="text-sm text-slate-500">
                        {TIME_SLOTS.find(s => s.id === slot.time_slot)?.label}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 italic">No specific dates selected</p>
            )
          )}
        </CardContent>
      </Card>

      {/* Quote Summary Card */}
      <div className="quote-summary-card">
        <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>
          Selected Services
        </h3>
        <div className="space-y-3 mb-6">
          {pricingConfig?.services
            .filter((s) => selectedServices[s.service_id])
            .map((service) => {
              const price = calculatedPrices[service.service_id];
              const hasWarning = service.service_id === 'windows' || service.service_id === 'gutters';
              return (
                <div key={service.service_id} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">{service.service_name}</span>
                    <div className="text-right">
                      {price?.requiresQuote ? (
                        <span className="text-amber-400 text-sm">Contact for quote</span>
                      ) : price?.savings > 0 ? (
                        <>
                          <span className="text-slate-500 line-through text-sm mr-2">${price.original}</span>
                          <span className="font-medium">${price.discounted}</span>
                        </>
                      ) : (
                        <span className="font-medium">${price?.original}</span>
                      )}
                    </div>
                  </div>
                  {service.service_id === 'windows' && (
                    <div className="flex items-center gap-1 text-amber-400 text-xs">
                      <AlertTriangle className="w-3 h-3" />
                      <span>Screens must be removed by owner prior to cleaning</span>
                    </div>
                  )}
                  {service.service_id === 'gutters' && (
                    <div className="flex items-center gap-1 text-amber-400 text-xs">
                      <AlertTriangle className="w-3 h-3" />
                      <span>Open gutters only - does not include gutters with screens</span>
                    </div>
                  )}
                </div>
              );
            })}
        </div>

        <div className="border-t border-slate-700 pt-4 space-y-2">
          <div className="flex justify-between text-slate-400">
            <span>Subtotal</span>
            <span data-testid="review-subtotal">${subtotal}</span>
          </div>
          {bundleDiscount > 0 && (
            <div className="flex justify-between text-emerald-400">
              <span className="flex items-center gap-2">
                <Gift className="w-4 h-4" />
                Bundle Discount
              </span>
              <span>-${bundleDiscount}</span>
            </div>
          )}
          {creditApplied > 0 && (
            <div className="flex justify-between text-emerald-400">
              <span className="flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Credit Applied ({creditCode})
              </span>
              <span>-${creditApplied}</span>
            </div>
          )}
          {promoDiscount > 0 && (
            <div className="flex justify-between text-emerald-400" data-testid="review-promo-discount-row">
              <span className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Promo Code ({promoCode})
              </span>
              <span>-${promoDiscount}</span>
            </div>
          )}
          <div className="flex justify-between text-2xl font-bold pt-2">
            <span>Total</span>
            <span className="text-yellow-400" data-testid="review-total">${finalTotal}</span>
          </div>
        </div>

        {bundleDiscount > 0 && (
          <div className="mt-4 p-3 bg-emerald-900/50 rounded-lg text-center">
            <p className="text-emerald-400 font-medium">
              You're saving ${bundleDiscount} with bundle pricing!
            </p>
          </div>
        )}

        {/* Credit Code Input */}
        <div className="mt-4 p-4 bg-slate-800 rounded-lg border border-slate-700">
          <div className="flex items-center gap-2 mb-3">
            <Tag className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-medium text-slate-300">Have a credit code?</span>
          </div>
          
          {creditInfo ? (
            <div className="bg-emerald-900/30 border border-emerald-500/50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-400 font-bold">${creditApplied} Credit Applied!</p>
                  <p className="text-xs text-emerald-300/70">Code: {creditCode}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={removeCreditCode}
                  className="text-slate-400 hover:text-red-400"
                >
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Input
                placeholder="Enter code (e.g., RS-A1B2C3)"
                value={creditCode}
                onChange={(e) => setCreditCode(e.target.value.toUpperCase())}
                className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 uppercase"
                data-testid="credit-code-input"
              />
              <Button
                onClick={validateCreditCode}
                disabled={isValidatingCode || !creditCode}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="apply-credit-btn"
              >
                {isValidatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* The Scrub Club - Coming Soon Banner */}
      {showScrubClub && (
        <Card className="border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-yellow-50 mt-6 overflow-hidden relative">
          <div className="absolute top-4 right-4 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
            COMING SOON
          </div>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-amber-900 mb-2">The Scrub Club</h3>
                <p className="text-amber-800 mb-4">
                  Get <strong>15% off</strong> House Siding + Driveway with our annual subscription.
                  Pay monthly and enjoy year-round clean!
                </p>
                <ul className="text-sm text-amber-700 space-y-2 mb-4">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600" />
                    15% discount on Siding + Driveway cleaning
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600" />
                    <strong>FREE</strong> Porch/Front Entry cleaning included!
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600" />
                    Convenient monthly payments
                  </li>
                </ul>
                <div className="bg-blue-100 border border-blue-300 rounded-lg p-3">
                  <p className="text-blue-800 text-sm font-medium">
                    We're launching The Scrub Club soon! Stay tuned for exclusive savings on your cleaning services.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* $50 Credit Offer Card */}
      <Card className="border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 to-green-50 mt-6 overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-2xl font-bold text-white">$50</span>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-emerald-900 mb-2">Get $50 Off Your Next Service!</h3>
              <p className="text-emerald-800 mb-3">
                Accept this estimate now and receive a <strong>$50 credit</strong> toward your next cleaning service at this property.
              </p>
              <ul className="text-sm text-emerald-700 space-y-2">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-600" />
                  Credit applied automatically on your next visit
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-600" />
                  Valid for 12 months from today
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-600" />
                  Exclusive to this property address
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customer Notes */}
      <Card className="border-slate-200 mt-6">
        <CardContent className="p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-400" />
            Additional Notes (Optional)
          </h3>
          <Textarea
            placeholder="Please list any special areas for cleaning not included in our standard services (e.g., detached garage, pool house, gazebo, etc.)"
            value={customerNotes}
            onChange={(e) => setCustomerNotes(e.target.value)}
            className="min-h-[100px] resize-none"
            data-testid="customer-notes"
          />
          <p className="text-xs text-slate-500 mt-2">
            Our team will review your notes and contact you to discuss additional services if needed.
          </p>
        </CardContent>
      </Card>

      {/* Pre-Service Walkthrough Notice */}
      <Card className="border-2 border-blue-300 bg-blue-50 mt-6">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <ClipboardCheck className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-blue-900 mb-2">Pre-Service Property Walkthrough</h3>
              <p className="text-blue-800 mb-3">
                Prior to beginning any work, our technician will walk the property to identify any potential hazards or problems that may either prevent work from being done or affect the estimate price.
              </p>
              <div className="bg-white border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-900 font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <span><strong>Important:</strong> Estimate price is subject to change after the on-site walkthrough.</span>
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Estimate Acceptance Checkbox */}
      <Card className="border-slate-200 mt-6">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <Checkbox
              id="accept_estimate"
              checked={estimateAccepted}
              onCheckedChange={(checked) => setEstimateAccepted(checked)}
              className="mt-1 h-5 w-5"
              data-testid="accept-estimate-checkbox"
            />
            <div>
              <Label 
                htmlFor="accept_estimate" 
                className="text-base font-medium cursor-pointer text-slate-900"
              >
                I accept this estimate total of ${total} and claim my $50 credit
              </Label>
              <p className="text-sm text-slate-500 mt-1">
                By checking this box, you acknowledge the estimated total and agree to proceed with scheduling.
                Final pricing may vary based on actual service conditions.
              </p>
              <p className="text-sm text-emerald-600 mt-3 flex items-center gap-2">
                <Gift className="w-4 h-4" />
                <span><strong>$50 credit</strong> will be issued for your next service at this address!</span>
              </p>
              <p className="text-sm text-blue-600 mt-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                <span>You'll also be subscribed FREE to <strong>The Shiny Bubble</strong> — our weekly newsletter!</span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
